import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Image as ImageIcon, Link as LinkIcon, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Campaign {
  id: number;
  title: string;
  message: string;
  image?: string;
  link?: string;
}

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [link, setLink] = useState("");
  const { toast } = useToast();

  const handleSave = () => {
    if (!title || !message) {
      toast({
        title: "Erro",
        description: "Título e mensagem são obrigatórios",
        variant: "destructive",
      });
      return;
    }

    const newCampaign: Campaign = {
      id: Date.now(),
      title,
      message,
      image: imageUrl,
      link,
    };

    setCampaigns([...campaigns, newCampaign]);
    clearForm();

    toast({
      title: "Sucesso",
      description: "Campanha criada com sucesso!",
    });
  };

  const clearForm = () => {
    setTitle("");
    setMessage("");
    setImageUrl("");
    setLink("");
  };

  const handleSend = (campaign: Campaign) => {
    // Aqui você implementaria a lógica de envio da campanha
    toast({
      title: "Iniciando campanha",
      description: "Sua campanha está sendo iniciada...",
    });
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">Campanhas</h2>
        <Button onClick={clearForm}>
          <Plus className="mr-2" /> Nova Campanha
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Nova Campanha</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Título da Campanha</Label>
            <Input
              id="title"
              placeholder="Digite o título"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Mensagem</Label>
            <Textarea
              id="message"
              placeholder="Digite sua mensagem..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="image">URL da Imagem</Label>
            <div className="flex gap-2">
              <Input
                id="image"
                placeholder="Cole a URL da imagem"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
              />
              <Button variant="outline" size="icon">
                <ImageIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="link">Link</Label>
            <div className="flex gap-2">
              <Input
                id="link"
                placeholder="Cole o link"
                value={link}
                onChange={(e) => setLink(e.target.value)}
              />
              <Button variant="outline" size="icon">
                <LinkIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <Button onClick={handleSave} className="w-full">
            Salvar Campanha
          </Button>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {campaigns.map((campaign) => (
          <Card key={campaign.id}>
            <CardHeader>
              <CardTitle>{campaign.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">{campaign.message}</p>
              {campaign.image && (
                <img
                  src={campaign.image}
                  alt="Campaign"
                  className="w-full h-40 object-cover rounded-md"
                />
              )}
              {campaign.link && (
                <a
                  href={campaign.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:underline block"
                >
                  {campaign.link}
                </a>
              )}
              <Button onClick={() => handleSend(campaign)} className="w-full">
                <Send className="mr-2" /> Iniciar Campanha
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Campaigns;
